import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Send, Pin, Trash2, BellOff, Bell } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/atoms";
import Gate from "@/components/Gate";

function MessageBubble({ m, me, isAdmin, onReact, onPin, onDelete }) {
  const mine = m.user_id === me.id;
  const pressTimer = useRef(null);
  const startPress = () => { pressTimer.current = setTimeout(() => onReact(m.id, "heart"), 500); };
  const endPress = () => { if (pressTimer.current) clearTimeout(pressTimer.current); };
  const fire = m.reactions?.fire?.length || 0;
  const heart = m.reactions?.heart?.length || 0;

  return (
    <div className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.id}`}>
      <Avatar src={m.author_picture} name={m.author_name} size={34} admin={m.is_admin} />
      <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
        <div className="flex items-center gap-2 mb-1">
          <span className="font-display font-bold text-xs">{m.author_name}</span>
          {m.is_admin && <span className="bg-[#8B1E28] text-[#F4F0E6] text-[8px] font-mono2 px-1 rounded uppercase">Admin</span>}
          {!m.is_admin && m.author_badge && <span className="bg-[#C7A656] text-[#121212] text-[8px] font-mono2 px-1 rounded uppercase border border-black">{m.author_badge}</span>}
        </div>
        <div
          className={`border-2 border-black rounded-lg px-3 py-2 select-none ${m.is_admin ? "bg-[#EAE4D3]" : mine ? "bg-[#C7A656]" : "bg-[#F9F6F0]"}`}
          onDoubleClick={() => onReact(m.id, "fire")}
          onTouchStart={startPress}
          onTouchEnd={endPress}
          onMouseDown={startPress}
          onMouseUp={endPress}
          onMouseLeave={endPress}
        >
          {m.image_url && <img src={fileUrl(m.image_url)} alt="" className="w-full max-h-52 object-cover border-2 border-black rounded mb-2" />}
          <p className="text-sm break-words">{m.text}</p>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <button onClick={() => onReact(m.id, "fire")} className="text-xs font-mono2 flex items-center gap-1" data-testid={`react-fire-${m.id}`}>🔥 {fire > 0 ? fire : ""}</button>
          <button onClick={() => onReact(m.id, "heart")} className="text-xs font-mono2 flex items-center gap-1" data-testid={`react-heart-${m.id}`}>🖤 {heart > 0 ? heart : ""}</button>
          <span className="font-mono2 text-[9px] text-[#554E40]">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
          {isAdmin && <button onClick={() => onPin(m.id)} data-testid={`pin-${m.id}`}><Pin size={12} /></button>}
          {(isAdmin || mine) && <button onClick={() => onDelete(m.id)} data-testid={`delete-msg-${m.id}`}><Trash2 size={12} /></button>}
        </div>
      </div>
    </div>
  );
}

export default function Chat() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [muted, setMuted] = useState(localStorage.getItem("atc_muted") === "1");
  const scrollRef = useRef(null);
  const lastCount = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/messages");
      if (data.length > lastCount.current && lastCount.current > 0 && !muted) {
        const latest = data[data.length - 1];
        if (latest && latest.user_id !== user.id) toast(`${latest.author_name}: ${latest.text.slice(0, 40)}`);
      }
      lastCount.current = data.length;
      setMessages(data);
    } catch (e) {}
  }, [muted, user?.id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const t = text;
    setText("");
    try { await api.post("/chat/messages", { text: t }); load(); } catch (err) { toast.error("Failed to send"); }
  };

  const react = async (id, emoji) => { try { await api.post(`/chat/messages/${id}/react?emoji=${emoji}`); load(); } catch (e) {} };
  const pin = async (id) => { try { await api.post(`/chat/messages/${id}/pin`); load(); } catch (e) {} };
  const del = async (id) => { try { await api.delete(`/chat/messages/${id}`); load(); toast.success("Deleted"); } catch (e) { toast.error("Not allowed"); } };
  const toggleMute = () => { const nv = !muted; setMuted(nv); localStorage.setItem("atc_muted", nv ? "1" : "0"); };

  const pinned = messages.filter((m) => m.pinned);
  const regular = messages.filter((m) => !m.pinned);

  return (
    <Gate>
      <div className="flex flex-col h-[calc(100vh-56px)]" data-testid="chat-page">
        <div className="px-4 py-2 border-b-2 border-black flex items-center justify-between bg-[#EAE4D3]">
          <h1 className="font-display font-black text-lg uppercase">Club Chat</h1>
          <button onClick={toggleMute} data-testid="chat-mute-btn" className="flex items-center gap-1 font-mono2 text-xs">
            {muted ? <BellOff size={16} /> : <Bell size={16} />} {muted ? "Muted" : "Live"}
          </button>
        </div>

        {pinned.length > 0 && (
          <div className="bg-[#F4E9C9] border-b-2 border-black p-3 flex flex-col gap-2" data-testid="chat-pinned">
            {pinned.map((m) => (
              <div key={m.id} className="flex items-start gap-2">
                <Pin size={13} className="mt-1 shrink-0" />
                <div className="flex-1">
                  <p className="text-xs"><b>{m.author_name}:</b> {m.text}</p>
                </div>
                {isAdmin && <button onClick={() => pin(m.id)} data-testid={`unpin-${m.id}`}><Pin size={12} fill="#8B1E28" /></button>}
              </div>
            ))}
          </div>
        )}

        <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 flex flex-col gap-4">
          {regular.length === 0 && <p className="text-center text-sm text-[#554E40] mt-10">No messages yet. Start the conversation.</p>}
          {regular.map((m) => (
            <MessageBubble key={m.id} m={m} me={user} isAdmin={isAdmin} onReact={react} onPin={pin} onDelete={del} />
          ))}
        </div>

        <form onSubmit={send} className="flex gap-2 p-3 border-t-2 border-black bg-[#EAE4D3]">
          <input className="atc-input flex-1" placeholder="Say something..." value={text} onChange={(e) => setText(e.target.value)} data-testid="chat-input" />
          <button type="submit" className="atc-btn btn-primary px-4" data-testid="chat-send-btn"><Send size={18} /></button>
        </form>
      </div>
    </Gate>
  );
}
